import { Check } from 'lucide-react';
import RecipeKatIcon from './RecipeKatIcon';
import RecipeStatusBadges from './RecipeStatusBadges';
import RecipeValidaceBadge from './RecipeValidaceBadge';
import { fmtCena, fmtDate, fmtTrvani, SECTION_COLOR } from './receptUtils';
import { MarzeBunka, NakladCenaBunka, ProdejBunka, KanalyBunka, StavBunka } from './PrehledBunky';
import { GRID, rozlozNazev, MARZE_BARVY } from './prehledGrid';

// Modrá = označeno pro hromadný přesun. Oranžová (SECTION_COLOR) zůstává
// „tohle mám otevřené" — dvě různé věci potřebují dvě různé barvy.
const VYBER_COLOR = '#3B82F6';

// Obohacená karta polotovaru/produktu v seznamu.
// `siroky` = přehled přes celou šířku → řádek se rozloží do sloupců.
export default function RecipeListRow({
    rec, active, selected = false, varCount, varianty = [], onClick,
    isDark, mutedColor, secondaryColor, depthPad = 0, onDragStartItem, siroky = false,
    prodej = null, kanalPokladna = false, kanalEshop = false, sloupce = null,
}) {
    const price = fmtCena(rec.total_cost);
    const jed = rec.jednotka?.zkratka || rec.jednotka_zkratka;
    const ingr = rec.kusovnik_count;
    const pouzito = rec.kusovniky_jako_ingredience_count;

    const metaBits = [];
    // Varianty první — jsou to ony, co odlišuje jinak stejně pojmenované řádky.
    if (varianty.length) metaBits.push(varianty.join(', '));
    if (ingr != null) metaBits.push(`${ingr} ingr.`);
    if (pouzito != null) metaBits.push(`použito ${pouzito}×`);

    const hlavniCena = rec.marze_kanaly?.rozvoz?.cena || rec.marze_kanaly?.bistro?.cena || rec.marze_kanaly?.vydej?.cena || null;
    // Problém = náklad přerostl prodejní cenu. Nic měkčího se červeně nemaluje,
    // jinak by červeně křičela polovina seznamu.
    const problem = siroky && hlavniCena > 0 && rec.total_cost > hlavniCena;

    const podklad = selected
        ? (isDark ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.10)')
        : active ? (isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.06)')
        : problem ? (isDark ? 'rgba(239,68,68,0.09)' : 'rgba(239,68,68,0.05)')
        : 'transparent';

    const spolecneProps = {
        'data-list-id': rec.id,
        onClick,
        draggable: !!onDragStartItem,
        onDragStart: e => { onDragStartItem?.(e, rec); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(rec.id)); } catch {} },
        className: `flex items-center cursor-pointer transition-colors ${siroky ? '' : 'gap-2.5'}`,
        style: siroky ? {
            gap: GRID.mezera,
            padding: '7px 10px',
            paddingLeft: 10 + depthPad,
            borderLeft: `3px solid ${selected ? VYBER_COLOR : (active ? SECTION_COLOR : (problem ? MARZE_BARVY.kriticka : 'transparent'))}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            background: podklad,
            opacity: rec.aktivni === false ? 0.6 : 1,
            userSelect: 'none',
        } : {
            padding: '8px 10px',
            // Zvýraznění (proužek + podklad) začíná v úrovni stromu, ne u kraje
            // panelu — jinak vybraná položka vypadá, že do kategorie nepatří.
            marginLeft: depthPad,
            borderLeft: selected ? `3px solid ${VYBER_COLOR}` : (active ? `3px solid ${SECTION_COLOR}` : '3px solid transparent'),
            background: podklad,
            opacity: rec.aktivni === false ? 0.6 : 1,
            userSelect: 'none', // ať Ctrl+klik označuje řádky, ne text
        },
        onMouseEnter: e => { if (!active && !selected) e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.025)'; },
        onMouseLeave: e => { if (!active && !selected) e.currentTarget.style.background = podklad; },
    };

    const ikona = (
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
            <RecipeKatIcon foto={rec.foto_url} ikona={rec.ikona || rec.kategorie?.ikona} kategorieNazev={rec.kategorie?.nazev} kategorieBarva={rec.kategorie?.barva} size={siroky ? 40 : 36} radius={siroky ? 10 : undefined} />
            {selected && (
                <span style={{
                    position: 'absolute', top: -4, left: -4, width: 16, height: 16, borderRadius: '50%',
                    background: VYBER_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 0 2px ${isDark ? '#1a1a26' : '#fff'}`,
                }}>
                    <Check size={10} strokeWidth={3} style={{ color: '#fff' }} />
                </span>
            )}
        </span>
    );

    // ── Přehled: řádek sloupcové mřížky ──
    if (siroky) {
        const { cislo, nazev } = rozlozNazev(rec.nazev);
        const meta = [...varianty, ingr != null ? `${ingr} ingr.` : null, fmtTrvani(rec.cas_s_celkem)].filter(Boolean);
        const prekroceni = problem ? Math.round((rec.total_cost / hlavniCena - 1) * 100) : null;

        return (
            <div {...spolecneProps}>
                {/* Pořadové číslo z názvu jako odznak — v názvu jen překáželo */}
                <div className="flex items-center justify-center" style={{ width: GRID.uchyt, flexShrink: 0 }}>
                    {cislo && (
                        <span className="text-[11px] font-semibold tabular-nums flex items-center justify-center"
                            style={{
                                minWidth: 26, height: 22, borderRadius: 5, padding: '0 5px', color: mutedColor,
                                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                            }}>{cislo}</span>
                    )}
                </div>
                {sloupce?.nahled !== false && <div style={{ width: GRID.nahled, flexShrink: 0, display: 'flex' }}>{ikona}</div>}

                <div className="min-w-0" style={{ flex: `${GRID.polozkaRust} 1 0`, minWidth: GRID.polozkaMin }}>
                    <div className="flex items-baseline gap-2 min-w-0">
                        <span className="text-[13px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{nazev}</span>
                        <span className="text-[10px] font-mono" style={{ color: mutedColor, flexShrink: 0 }}>{rec.kod}</span>
                        {problem && (
                            <span className="text-[10px] font-semibold" style={{
                                flexShrink: 0, padding: '1px 6px', borderRadius: 999,
                                background: `${MARZE_BARVY.kriticka}${isDark ? '2e' : '1f'}`, color: MARZE_BARVY.kriticka,
                            }}>náklad +{prekroceni} %</span>
                        )}
                        <RecipeStatusBadges aktivni={rec.aktivni} varCount={varCount} pouzitoCount={pouzito} />
                    </div>
                    <div className="text-[11px] truncate" style={{ color: mutedColor, marginTop: 1 }}>
                        {meta.join(' · ') || ' '}
                    </div>
                </div>

                {sloupce?.kanaly !== false && (
                    <KanalyBunka pokladna={kanalPokladna} eshop={kanalEshop} chybiCena={rec.total_cost == null}
                        mutedColor={mutedColor} isDark={isDark} />
                )}
                <NakladCenaBunka naklad={rec.total_cost} cena={hlavniCena} jednotka={jed}
                    mutedColor={mutedColor} isDark={isDark} />
                <MarzeBunka marze={rec.marze_kanaly} mutedColor={mutedColor} isDark={isDark} />
                {sloupce?.prodej !== false && <ProdejBunka prodej={prodej} mutedColor={mutedColor} isDark={isDark} />}
                {sloupce?.revize !== false && (
                    <div className="text-[11px] tabular-nums text-right" style={{ width: GRID.revize, flexShrink: 0, color: mutedColor, fontFamily: 'ui-monospace, monospace' }}>
                        {rec.posledni_revize_at ? fmtDate(rec.posledni_revize_at) : '—'}
                    </div>
                )}
                <StavBunka validace={rec.validace} mutedColor={mutedColor} />
            </div>
        );
    }

    // ── Úzký sloupec vedle detailu: dvouřádková karta ──
    return (
        <div {...spolecneProps}>
            {ikona}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <RecipeValidaceBadge validace={rec.validace} size={15} />
                    <span className="text-[13px] font-semibold flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>{rec.nazev}</span>
                    <span className="text-[10px] font-mono" style={{ color: mutedColor, flexShrink: 0 }}>{rec.kod}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] flex-1 min-w-0 truncate" style={{ color: mutedColor }}>
                        {metaBits.join(' · ') || ' '}
                    </span>
                    <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                        <RecipeStatusBadges aktivni={rec.aktivni} varCount={varCount} pouzitoCount={pouzito} />
                        {price
                            ? <span className="text-[12px] font-bold" style={{ color: 'var(--text-primary)' }}>{price}{jed ? <span className="font-normal" style={{ color: mutedColor }}>/{jed}</span> : null}</span>
                            : <span className="text-[11px]" style={{ color: mutedColor }} title="Chybí kalkulace">—</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}
